import React, { useEffect, useState } from 'react';
import {
  ListBase, ListView, FunctionField, TextInput, SelectInput, AutocompleteInput,
  useTranslate, useNotify, usePermissions,
} from 'react-admin';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useImportFlow } from '@/components/common/ImportDialog';
import {
  ExportFormatDialog, FilterToggle, HelpDialog, ListScreen, PageTitle, ResponsiveDatagrid,
  SectionHeader, useActiveFilterCount, type ExportFormat,
} from '@/components/shell';
import { USER_ROLES } from '@/config/constants';
import { httpClient } from '@/utils/httpClient';
import { downloadFile, type FileFormat } from '@/utils/jsonFileUtils';
import { BothRowBadge } from './BothRowBadge';
import { RuleSummary, sourceName } from './RuleSummary';
import { useDocumentSideChoices } from './documentSides';

interface Choice {
  id: string;
  name: string;
}

interface FieldMappingRecord {
  id: string;
  documentSide: string;
  documentTypeResponse?: { id: string; name?: string };
  nationalityResponse?: { id: string; name?: string };
  mappingFields?: Array<{
    fieldName?: string;
    standardFieldName?: string;
    regex?: string;
    kycMappingField?: Array<{ kycField?: { name?: string } | null; prefix?: string }>;
  }>;
}

type FieldMappingItem = {
  documentType: string;
  nationality: string;
  documentSide: string;
  mappingFields: Array<{
    fieldName: string;
    regex?: string;
    kycMappingField: Array<{ kycFieldName: string; prefix?: string; suffix?: string }>;
  }>;
};

const VISIBLE_RULES = 4;

const matchName = (filter: string, choice: Choice) => choice.name.toLowerCase().includes(filter.toLowerCase());

/** Front and back read as plain words; only the generated combined row is marked. */
const SideCell: React.FC<{ side: string }> = ({ side }) => {
  const choices = useDocumentSideChoices();
  if (side === 'Both') return <BothRowBadge />;
  return <>{choices.find((c) => c.id === side)?.name ?? side}</>;
};

const DocumentCell: React.FC<{ record: FieldMappingRecord }> = ({ record }) => (
  <Box component="span" sx={{ display: 'block' }}>
    <Typography variant="body2" component="span" sx={{ display: 'block' }}>{record?.documentTypeResponse?.name}</Typography>
    <Typography variant="body2" component="span" color="text.secondary" sx={{ display: 'block' }}>
      {record?.nationalityResponse?.name}
    </Typography>
  </Box>
);

const RulesCell: React.FC<{ record: FieldMappingRecord }> = ({ record }) => {
  const t = useTranslate();
  const fields = record?.mappingFields ?? [];
  if (!fields.length) return <Typography variant="body2" color="text.secondary" component="span">—</Typography>;
  return (
    <Stack spacing={0.25} component="span">
      {fields.slice(0, VISIBLE_RULES).map((f, i) => (
        <Box component="span" key={i} sx={{ display: 'block' }}>
          <RuleSummary
            output={f.fieldName || f.standardFieldName || ''}
            sources={(f.kycMappingField ?? []).map(sourceName).filter(Boolean)}
            regex={f.regex}
          />
        </Box>
      ))}
      {fields.length > VISIBLE_RULES && (
        <Typography variant="body2" color="text.secondary" component="span">
          {t('common.more', { count: fields.length - VISIBLE_RULES })}
        </Typography>
      )}
    </Stack>
  );
};

const FieldMappingHelp: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const t = useTranslate();
  const sides = useDocumentSideChoices();
  const sideHelp: Record<string, string> = {
    Front: t('fields-mapping.help_front'),
    Back: t('fields-mapping.help_back'),
    Both: t('fields-mapping.help_both'),
  };
  return (
    <HelpDialog open={open} onClose={onClose} title={t('fields-mapping.help_title')}>
      <Typography variant="body2">{t('fields-mapping.help_intro')}</Typography>
      <Typography variant="subtitle2">{t('fields-mapping.help_columns_title')}</Typography>
      <Box>
        <Typography variant="body2">{t('fields-mapping.help_doc')}</Typography>
        <Typography variant="body2" color="text.secondary">{t('fields-mapping.help_doc_desc')}</Typography>
      </Box>
      <Box>
        <Typography variant="body2">{t('fields-mapping.help_side')}</Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          {sides.map((side) => (
            <Typography component="li" variant="body2" color="text.secondary" key={side.id}>
              {side.name} {sideHelp[side.id]}
            </Typography>
          ))}
        </Box>
      </Box>
      <Box>
        <Typography variant="body2">{t('fields-mapping.help_fields')}</Typography>
        <Typography variant="body2" color="text.secondary">{t('fields-mapping.help_fields_desc')}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">{t('fields-mapping.help_tip')}</Typography>
    </HelpDialog>
  );
};

const parseImport = (data: unknown, format: FileFormat): FieldMappingItem[] | null => {
  if (format === 'json') {
    const raw = Array.isArray(data) ? data : (data as { items?: unknown })?.items;
    return Array.isArray(raw) ? (raw as FieldMappingItem[]) : null;
  }
  if (!Array.isArray(data)) return null;
  return (data as Array<Record<string, string>>).map((r) => ({
    documentType: r.documentType ?? r.documentTypeName ?? '',
    nationality: r.nationality ?? r.nationalityName ?? '',
    documentSide: r.documentSide ?? 'Front',
    mappingFields: r.mappingFields ? JSON.parse(r.mappingFields) : [],
  }));
};

/** Rendered inside the list's context so the filter badge counts the active filters. */
const Header: React.FC<{ filtersOpen: boolean; onToggleFilters: () => void }> = ({ filtersOpen, onToggleFilters }) => {
  const t = useTranslate();
  const navigate = useNavigate();
  const notify = useNotify();
  const activeCount = useActiveFilterCount();
  const { permissions } = usePermissions<string>();
  const isAdmin = permissions === USER_ROLES.ADMIN;
  const [helpOpen, setHelpOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const importFlow = useImportFlow({ endpoint: '/admin/fields-mapping/Import', parse: parseImport });

  const handleExport = async (format: ExportFormat) => {
    try {
      const r = await httpClient.get<{ items?: FieldMappingItem[] }>('/admin/fields-mapping/Export');
      const payload = r.data || {};
      const items = payload.items || [];
      const filename = `field-mappings-${new Date().toISOString().slice(0, 10)}`;
      if (format === 'csv') {
        const flat = items.map((it) => ({
          documentType: it.documentType,
          nationality: it.nationality,
          documentSide: it.documentSide,
          mappingFields: JSON.stringify(it.mappingFields),
        }));
        downloadFile(flat, filename, 'csv');
      } else {
        downloadFile(payload as unknown as Record<string, unknown>[], filename, 'json');
      }
      notify(t('screens.fieldMappings.exported', { smart_count: items.length }), { type: 'success' });
    } catch {
      notify('screens.fieldMappings.exportFailed', { type: 'error' });
    }
  };

  return (
    <>
      <SectionHeader
        left={
          <PageTitle
            title={t('resources.fields-mapping.name', { smart_count: 2 })}
            subtitle={t('screens.fieldMappings.subtitle')}
          />
        }
        help={{ label: t('screens.fieldMappings.help'), onClick: () => setHelpOpen(true) }}
        filters={<FilterToggle open={filtersOpen} onToggle={onToggleFilters} activeCount={activeCount} />}
        export={isAdmin ? { label: t('ra.action.export'), onClick: () => setExportOpen(true) } : undefined}
        actions={isAdmin ? [{ label: t('common.import'), onClick: importFlow.start, disabled: importFlow.importing }] : undefined}
        create={{ label: t('common.create'), onClick: () => navigate('/fields-mapping/create') }}
      />
      <FieldMappingHelp open={helpOpen} onClose={() => setHelpOpen(false)} />
      <ExportFormatDialog open={exportOpen} onClose={() => setExportOpen(false)} onExport={handleExport} />
      {importFlow.dialog}
    </>
  );
};

export const FieldMappingList = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const sideChoices = useDocumentSideChoices();
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [standardFields, setStandardFields] = useState<Choice[]>([]);
  const [nationalities, setNationalities] = useState<Choice[]>([]);
  const [documentTypes, setDocumentTypes] = useState<Choice[]>([]);

  useEffect(() => {
    httpClient.get<Array<{ fieldName: string }>>('/admin/fields-mapping/GetStandardFields').then((r) => {
      const unique = [...new Set((r.data || []).map((f) => f.fieldName))].sort();
      setStandardFields(unique.map((name) => ({ id: name, name })));
    });
    httpClient.get<Choice[]>('/admin/nationality/GetAllNationality').then((r) => {
      setNationalities((r.data || []).map((n) => ({ id: n.id, name: n.name })));
    });
    httpClient.get<Choice[]>('/admin/document-type/GetAllDocumentTypes').then((r) => {
      setDocumentTypes((r.data || []).map((d) => ({ id: d.id, name: d.name })));
    });
  }, []);

  const filters = [
    <TextInput key="q" source="q" label={t('ra.action.search')} alwaysOn resettable />,
    ...(filtersOpen
      ? [
          <AutocompleteInput key="docType" source="DocumentTypeId" label={t('screens.fieldMappings.documentType')}
            choices={documentTypes} alwaysOn matchSuggestion={matchName} />,
          <AutocompleteInput key="nationality" source="NationalityId" label={t('screens.fieldMappings.nationality')}
            choices={nationalities} alwaysOn matchSuggestion={matchName} />,
          <SelectInput key="side" source="DocumentSide" label={t('resources.fields-mapping.fields.side')}
            choices={sideChoices} alwaysOn emptyText={t('common.filters.all')} />,
          <SelectInput key="field" source="StandardFieldName" label={t('resources.fields-mapping.fields.standardField')}
            choices={standardFields} alwaysOn emptyText={t('common.filters.all')} />,
        ]
      : []),
  ];

  return (
    <ListBase perPage={25}>
      <ListScreen header={<Header filtersOpen={filtersOpen} onToggleFilters={() => setFiltersOpen((o) => !o)} />}>
        <ListView filters={filters} actions={false} empty={false}>
          <ResponsiveDatagrid<FieldMappingRecord>
            rowClick="edit"
            mobile={{
              primary: (r) => r.documentTypeResponse?.name,
              status: (r) => <SideCell side={r.documentSide} />,
              lines: [
                { label: t('screens.fieldMappings.nationality'), render: (r) => r.nationalityResponse?.name },
                { label: t('resources.fields-mapping.fields.standardFields'), render: (r) => <RulesCell record={r} /> },
              ],
              onClick: (r) => navigate(`/fields-mapping/${r.id}`),
            }}
          >
            <FunctionField
              sortBy="documentType"
              label={t('resources.fields-mapping.fields.document')}
              render={(r: FieldMappingRecord) => <DocumentCell record={r} />}
            />
            <FunctionField
              sortBy="documentSide"
              label={t('resources.fields-mapping.fields.side')}
              render={(r: FieldMappingRecord) => <SideCell side={r.documentSide} />}
            />
            <FunctionField
              sortable={false}
              label={t('resources.fields-mapping.fields.standardFields')}
              render={(r: FieldMappingRecord) => <RulesCell record={r} />}
            />
          </ResponsiveDatagrid>
        </ListView>
      </ListScreen>
    </ListBase>
  );
};
